import { fetchArchivedDealsBulkByDealIds } from '@/hooks/fetch-store-and-stitch/use-archived-deals.ts';
import type { Notification } from '@/models/registry';
import { dealStore, notificationStore } from '@/models/stores/index';
import type { Deal, User } from '@/models/types';
import { isEmpty } from 'lodash';
import { toJS } from 'mobx';
import { useEffect, useMemo } from 'react';

export type AugmentedNotification = Notification & {
  isRead: boolean;
  deal: Deal;
  user: User | undefined;
  triggeredByUser: User | undefined;
};

const useNotifications = (): {
  notifications: Notification[];
  numberOfUnreadNotifications: number;
} => {
  const notificationsProxy = notificationStore.all;
  const notifications = toJS(notificationsProxy);

  useEffect(() => {
    const dealsToFetch = new Set<string>();

    notifications.forEach((notification) => {
      if (!notification.deal && notification.deal_id) {
        dealsToFetch.add(notification.deal_id);
      }
    });

    if (isEmpty(dealsToFetch)) return;

    (async () => {
      try {
        const archivedDeals = await fetchArchivedDealsBulkByDealIds(
          Array.from(dealsToFetch),
        );
        dealStore.upsertArchivedDealsBulk(archivedDeals);
      } catch (error) {
        console.error(
          '[useNotifications] Error fetching archived deals',
          error,
        );
      }
    })();
  }, [
    JSON.stringify(
      notifications
        .filter((n) => n.deal_id)
        .map((n) => n.deal_id)
        .sort(),
    ),
  ]);

  const numberOfUnreadNotifications = useMemo(() => {
    return notifications.filter((notification) => !notification.isRead).length;
  }, [JSON.stringify(notifications)]);

  return {
    notifications,
    numberOfUnreadNotifications,
  };
};

export default useNotifications;
